import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikHelpers, FormikValues } from 'formik';
import toast from 'react-hot-toast';
import {
  FormSchema,
  initialValues,
  IQrEntry,
  TextAreaStyles,
} from './constants';
import { Button } from '../../../components/Button/Button';
import { IconCross } from '../../../utils/icons';
import FieldInput from '../../pro/createEditProfile/formFragments/FieldInput';
import FieldCheckbox from '../../pro/createEditProfile/formFragments/FieldCheckbox';
import FormRow from '../../pro/createEditProfile/formFragments/row';
import { ImageProps } from '../../brand/BrandForm';
import { QrImage } from './QrImage';
import axiosInstant from '../../../api';
import config from '../../../config';
import { qrCodeEndpoint } from '../../../api/endpoints';
import { FieldTextArea } from '../../../components/fields/TextArea/TextArea';

interface Props {
  entry?: IQrEntry;
  onUpdate: (entry: IQrEntry) => void;
  onClose: () => void;
}

export const QrForm: FC<Props> = ({ entry, onUpdate, ...rest }) => {
  const [initValues, setInitialValues] = useState(entry || initialValues);
  const [image, setImage] = useState<ImageProps>();

  useEffect(() => {
    if (entry?.id) {
      setInitialValues({ ...entry });
      if (entry.branded_image)
        setImage({ source: entry.branded_image, error: '' });
    }
  }, [entry]);

  const handleSubmit = async (
    values: FormikValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>
  ) => {
    let url = `${config.API_URL}${qrCodeEndpoint}`;
    if (entry?.id) url += `/${entry.id}`;
    const payload = { ...entry, ...values };
    if (image) payload.branded_image = image.source;
    setSubmitting(true);

    try {
      const { data } = await axiosInstant({
        method: entry?.id ? 'PATCH' : 'POST',
        url,
        data: payload,
      });
      onUpdate(data);
      rest.onClose?.();
    } catch (e) {
      toast.error('Failed to submit form');
    }
    setSubmitting(false);
  };

  return (
    <Dialog
      {...rest}
      open
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          m: { xs: 1.5, sm: 4 },
          width: { xs: 'calc(100% - 24px)', sm: 'auto' },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
          borderRadius: 2,
        },
      }}
    >
      <Box className="flex justify-between items-center px-4">
        <DialogTitle
          className="text-text-1 text-lg !px-0"
          fontFamily="CircularStdBlack"
        >
          {entry ? 'Edit' : 'Create'} QR
        </DialogTitle>
        <IconButton onClick={rest.onClose}>
          <IconCross />
        </IconButton>
      </Box>
      <hr style={{ margin: '0px 16px', color: 'var(--border)' }} />

      <DialogContent>
        <Formik
          initialValues={initValues}
          enableReinitialize
          validationSchema={FormSchema}
          onSubmit={handleSubmit}
        >
          <Form className="flex flex-col gap-6">
            <Stack spacing={2} gap={1}>
              <FormRow count={2}>
                <FieldInput name="name" required />
                <FieldInput name="url" label="Destination URL" required />
              </FormRow>
              <FieldTextArea name="description" {...TextAreaStyles} />
              <FieldCheckbox name="deactivated" text="Deactivated" />
            </Stack>
            {/* disabling till cors issue resolved */}
            {false && <QrImage image={image} setImage={setImage} />}
            <Divider />
            <Box className="flex items-center justify-end">
              <Button
                size="large"
                type="submit"
                variant="contained"
                className="!capitalize"
              >
                Save changes
              </Button>
            </Box>
          </Form>
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
